import { useState } from 'react';
import { get, post } from '../api';
import { ComposeButtons, type ComposeLink } from '../components/ComposeButtons';
import { Problems } from '../components/Field';
import { toast } from '../components/toast';
import { describe } from '../errors';
import { useLoad } from '../hooks';

interface Action {
  trigger: string;
  label: string;
  channel: 'email' | 'sms';
  contact_id: number;
  recipient_name: string;
  mode: 'auto' | 'manual';
  available: boolean;
  reason?: string;
}

interface HistoryRow {
  id: number;
  trigger_label: string;
  channel: 'email' | 'sms';
  recipient_name: string;
  status: string;
  error: string | null;
  handled_at: string | null;
  created_at: string;
}

interface Composed {
  id: number;
  channel: 'email' | 'sms';
  to: string;
  subject: string;
  body: string;
  href: string;
}

const STATUS: Record<string, string> = {
  pending: 'Waiting to be sent',
  sending: 'Sending',
  sent: 'Sent',
  failed: 'Failed',
  skipped: 'Not sent',
  expired: 'No longer needed',
};

const channelName = (c: 'email' | 'sms') => (c === 'email' ? 'email' : 'SMS');
const keyOf = (a: Pick<Action, 'trigger' | 'channel' | 'contact_id'>) =>
  `${a.trigger}:${a.channel}:${a.contact_id}`;

/** A visit's messages: send or prepare each one that is switched on, and what has gone so far. */
export function VisitMessages(props: { visitId: number }) {
  const [tick, setTick] = useState(0);
  const loaded = useLoad(
    () =>
      get<{ actions: Action[]; history: HistoryRow[] }>(`/api/visits/${props.visitId}/messages`),
    [props.visitId, tick],
  );
  const [open, setOpen] = useState<{ key: string; composed: Composed } | null>(null);
  const [busy, setBusy] = useState('');
  const [problems, setProblems] = useState<string[]>([]);

  const data = loaded.data;
  // Nothing switched on and nothing sent yet: no section at all.
  if (!data || !Array.isArray(data.actions) || !Array.isArray(data.history)) return null;
  if (data.actions.length === 0 && data.history.length === 0) return null;

  async function act(a: Action) {
    const key = keyOf(a);
    setBusy(key);
    setProblems([]);
    try {
      const r = await post<{ status?: string; composed?: Composed }>(
        `/api/visits/${props.visitId}/messages`,
        { trigger: a.trigger, channel: a.channel, contact_id: a.contact_id },
      );
      if (r.composed) setOpen({ key, composed: r.composed });
      else {
        toast(r.status === 'sent' ? 'Sent.' : `Not sent: ${r.status ?? 'unknown'}.`);
        setTick((t) => t + 1);
      }
    } catch (err) {
      setProblems(describe(err).problems);
    } finally {
      setBusy('');
    }
  }

  async function done(status: 'sent' | 'skipped') {
    if (!open) return;
    try {
      await post(`/api/messages/${open.composed.id}/${status === 'sent' ? 'sent' : 'skip'}`);
      setOpen(null);
      setTick((t) => t + 1);
    } catch (err) {
      setProblems(describe(err).problems);
    }
  }

  return (
    <section className="panel stack" aria-labelledby="visit-messages-h">
      <h2 id="visit-messages-h">Messages</h2>
      <Problems problems={problems} />
      {data.actions.length > 0 && (
        <ul className="plain message-actions">
          {data.actions.map((a) => {
            const key = keyOf(a);
            const verb = a.mode === 'auto' ? 'Send' : 'Prepare';
            return (
              <li key={key} className="stack-tight">
                <div className="row wrap">
                  <span>
                    <strong>{a.label}</strong>{' '}
                    <span className="muted">
                      to {a.recipient_name} by {channelName(a.channel)}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="quiet"
                    disabled={!a.available || busy === key}
                    aria-label={`${verb} ${a.label} to ${a.recipient_name} by ${channelName(a.channel)}`}
                    onClick={() => void act(a)}
                  >
                    {verb}
                  </button>
                  {a.reason && <span className="muted">{a.reason}</span>}
                </div>
                {open?.key === key && (
                  <ComposeButtons
                    items={[open.composed as ComposeLink]}
                    onDone={(s) => void done(s)}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
      {data.history.length > 0 && (
        <div className="table-scroll">
          <table>
            <caption className="sr-only">Messages for this visit</caption>
            <thead>
              <tr>
                <th>Message</th>
                <th>To</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {data.history.map((h) => (
                <tr key={h.id}>
                  <td>
                    {h.trigger_label} ({channelName(h.channel)})
                  </td>
                  <td>{h.recipient_name}</td>
                  <td>
                    {STATUS[h.status] ?? h.status}
                    {h.error && h.status !== 'sent' ? `: ${h.error}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
